import React, { useEffect, useRef, useState } from "react";
import { Music2, Loader2, Upload, CheckCircle2, Link2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";

const TIKTOK_LOGIN_URL = `${API_BASE}/connections/tiktok/login`;

const TikTokPost = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/connections/tiktok/status");
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const publish = async () => {
    if (!file) return toast.error("Pick a video file first.");
    setPublishing(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("video", file);
      fd.append("caption", caption);
      const { data } = await api.post("/connections/tiktok/publish-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setResult(data);
      toast.success("Video sent to TikTok (private).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "TikTok publish failed.");
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="tiktok-post-loading">
        <Loader2 size={16} className="animate-spin" /> Loading TikTok…
      </div>
    );
  }

  const connected = status?.connected;
  const canPublish = connected; // scope enforced server-side

  return (
    <div className="max-w-2xl space-y-6" data-testid="tiktok-post-panel">
      <div className="bg-[#0A192F] border border-white/10 rounded-sm p-6">
        <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.3em] mb-3">
          <Music2 size={14} /> Post video to TikTok
        </div>
        <h3 className="font-heading text-2xl font-bold text-white">Upload a video to TikTok</h3>
        <p className="mt-1 text-slate-400 text-sm">
          Publishes straight to your connected TikTok via TikTok's own Content Posting API. While the
          app is under review, posts go out as <strong>private (only you)</strong>.
        </p>

        {/* connection state */}
        <div className="mt-5 flex items-center gap-3 p-3 bg-[#050B14] border border-white/10 rounded-sm">
          {connected ? (
            <>
              <CheckCircle2 size={18} className="text-emerald-400" />
              <span className="text-sm text-slate-200" data-testid="tiktok-connected">
                Connected {status?.display_name ? `as @${status.display_name}` : ""}
              </span>
            </>
          ) : (
            <>
              <ShieldAlert size={18} className="text-amber-400" />
              <span className="text-sm text-slate-300" data-testid="tiktok-not-connected">
                TikTok not connected.
              </span>
              <a
                href={TIKTOK_LOGIN_URL}
                data-testid="tiktok-connect-btn"
                className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold text-xs rounded-sm hover:bg-[#33F3FF]"
              >
                <Link2 size={14} /> Connect TikTok
              </a>
            </>
          )}
        </div>

        {connected && (
          <div className="mt-5 space-y-3">
            <input
              ref={fileRef}
              type="file"
              accept="video/mp4,video/quicktime,video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="tiktok-video-input"
              className="hidden"
            />
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="tiktok-pick-video"
              className="w-full px-4 py-8 border border-dashed border-cyan-400/40 hover:bg-cyan-400/5 text-cyan-200 rounded-sm flex flex-col items-center gap-2"
            >
              <Upload size={22} />
              <span className="text-sm">{file ? file.name : "Choose a video (MP4, under 64MB)"}</span>
            </button>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={3}
              placeholder="Caption (optional)"
              data-testid="tiktok-caption"
              className="w-full px-4 py-3 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white placeholder-slate-500 text-sm rounded-sm resize-none"
            />
            <button
              onClick={publish}
              disabled={publishing || !file || !canPublish}
              data-testid="tiktok-publish-btn"
              className="w-full px-5 py-3 bg-[#00F0FF] text-[#050B14] font-bold text-sm rounded-sm hover:bg-[#33F3FF] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {publishing ? <Loader2 size={16} className="animate-spin" /> : <Music2 size={16} />}
              {publishing ? "Uploading to TikTok…" : "Publish to TikTok"}
            </button>
          </div>
        )}

        {result && (
          <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-sm" data-testid="tiktok-result">
            <p className="text-emerald-300 text-sm font-bold">Sent to TikTok ✓</p>
            <p className="text-slate-300 text-xs mt-1">{result.note}</p>
            {result.publish_id && (
              <p className="text-slate-500 text-[11px] mt-1 font-mono">publish_id: {result.publish_id}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TikTokPost;
